import React, { useState } from 'react';
import * as S from './TimeRangeModal.styled';
import EscapeButton from 'pages/components/EscapeButton/EscapeButton';
import Calendar from '@mui/icons-material/DateRange';
import AccessTime from '@mui/icons-material/AccessTime';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import DatePicker from 'react-calendar';
import {
  convertDate22,
  convertTime1,
  getTimeFormat,
  getTimeSetFormat,
  isTimeFormat,
} from 'utils';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectMetricTime } from 'pages/redux';
import {
  selectSingleLogTime,
  setAssociateElevatedActivityLogs,
  setComplianceActivity,
  setSingleLogTime,
} from '../redux';
import { oneYearAgo } from '../hooks';

type TimeRangeModalProps = {
  isOn: boolean,
  // eslint-disable-next-line no-unused-vars
  onApply: (next: { isOn: boolean }) => void,
  onExit: () => void,
};

type PickMode = 'start' | 'end' | null;

/** Full-day start when time-of-day is not set. */
const toDayStart = (d: Date) => {
  const next = new Date(d);
  next.setHours(0, 0, 0, 0);
  return next;
};

/** Full-day end when time-of-day is not set. */
const toDayEnd = (d: Date) => {
  const next = new Date(d);
  next.setHours(23, 59, 59, 999);
  return next;
};

const isDefaultStartTime = (d: Date) => (
  d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0
);

const isDefaultEndTime = (d: Date) => (
  d.getHours() === 23 && d.getMinutes() === 59
);

/** Keep calendar day, apply hours/minutes from `time`. */
const applyClock = (day: Date, time: Date) => {
  const next = new Date(day);
  next.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return next;
};

const sameInstant = (a: Date, b: Date) => +a === +b;

const resolveInitialRange = (
  saved: { startDate?: Date | string; endDate?: Date | string } | null | undefined,
  metric: { startDate?: Date | string; endDate?: Date | string } | null | undefined,
) => {
  const startSrc = saved?.startDate || metric?.startDate || new Date();
  const endSrc = saved?.endDate || metric?.endDate || new Date();
  const start = new Date(startSrc);
  const end = new Date(endSrc);
  // Only force day bounds when source had no meaningful custom clock.
  const startHadCustom = saved?.startDate
    ? !isDefaultStartTime(new Date(saved.startDate))
    : false;
  const endHadCustom = saved?.endDate
    ? !isDefaultEndTime(new Date(saved.endDate))
    : false;
  return {
    start: startHadCustom ? start : toDayStart(start),
    end: endHadCustom ? end : toDayEnd(end),
    useTimeOfDay: startHadCustom || endHadCustom,
  };
};

const TimeRangeModal = ({
  isOn,
  onApply,
  onExit,
}: TimeRangeModalProps) => {
  const dispatch = useAppDispatch();
  const metricTime = useAppSelector(selectMetricTime);
  const savedRange = useAppSelector(selectSingleLogTime);
  const initial = resolveInitialRange(savedRange, metricTime);

  const [enabled, setEnabled] = useState(isOn);
  const [startD, setStartD] = useState<Date>(initial.start);
  const [endD, setEndD] = useState<Date>(initial.end);
  // Collapsed until user needs clock precision (or already had custom times).
  const [useTimeOfDay, setUseTimeOfDay] = useState(initial.useTimeOfDay);
  const [pickMode, setPickMode] = useState<PickMode>(null);

  const normalizePair = (start: Date, end: Date, withTime: boolean) => {
    const build = (a: Date, b: Date) => {
      if (!withTime) return { start: toDayStart(a), end: toDayEnd(b) };
      const s = new Date(a);
      s.setSeconds(0, 0);
      const e = new Date(b);
      e.setSeconds(59, 999);
      return { start: s, end: e };
    };
    let pair = build(start, end);
    if (+pair.start <= +pair.end) return pair;
    // Start after end: swap days, keep each side's clock when time-of-day is on.
    if (withTime) {
      const s = applyClock(end, start);
      s.setSeconds(0, 0);
      const e = applyClock(start, end);
      e.setSeconds(59, 999);
      pair = +s <= +e ? { start: s, end: e } : build(end, start);
    } else {
      pair = build(end, start);
    }
    return pair;
  };

  const onUseMetricsTime = () => {
    const s = metricTime?.startDate
      ? toDayStart(new Date(metricTime.startDate))
      : toDayStart(new Date());
    const e = metricTime?.endDate
      ? toDayEnd(new Date(metricTime.endDate))
      : toDayEnd(new Date());
    const next = normalizePair(s, e, false);
    setStartD(next.start);
    setEndD(next.end);
    setUseTimeOfDay(false);
    setEnabled(true);
  };

  const onToggleTimeOfDay = () => {
    if (useTimeOfDay) {
      // Collapse → full-day bounds again.
      setStartD(toDayStart(startD));
      setEndD(toDayEnd(endD));
      setUseTimeOfDay(false);
      return;
    }
    // Expand with sensible defaults (start of day / end of day clocks).
    setStartD(applyClock(startD, toDayStart(startD)));
    setEndD(applyClock(endD, toDayEnd(endD)));
    setUseTimeOfDay(true);
  };

  const setClockPart = (
    which: 'start' | 'end',
    part: 'hours' | 'minutes',
    raw: string,
  ) => {
    const max = part === 'hours' ? 23 : 59;
    if (!isTimeFormat(raw, max)) return;
    const value = Number(getTimeSetFormat(raw, max));
    if (which === 'start') {
      const next = new Date(startD);
      if (part === 'hours') next.setHours(value);
      else next.setMinutes(value);
      next.setSeconds(0, 0);
      setStartD(next);
      return;
    }
    const next = new Date(endD);
    if (part === 'hours') next.setHours(value);
    else next.setMinutes(value);
    next.setSeconds(59, 999);
    setEndD(next);
  };

  const onSave = () => {
    const next = normalizePair(startD, endD, useTimeOfDay);
    const prevStart = savedRange?.startDate
      ? new Date(savedRange.startDate)
      : null;
    const prevEnd = savedRange?.endDate
      ? new Date(savedRange.endDate)
      : null;
    const datesChanged = !prevStart || !prevEnd
      || !sameInstant(prevStart, next.start)
      || !sameInstant(prevEnd, next.end);

    // Compliance elevated / activity view ends if range is turned off or dates are changed.
    if (!enabled || datesChanged) {
      dispatch(setAssociateElevatedActivityLogs(false));
      dispatch(setComplianceActivity({ active: false }));
    }

    // Off must clear the saved range — otherwise LogTable forces the toggle back on.
    // Activity-log lock is separate; calendar Time does not unlock start/end logs.
    if (enabled) {
      dispatch(setSingleLogTime({ startDate: next.start, endDate: next.end }));
    } else {
      dispatch(setSingleLogTime(null));
    }
    onApply({ isOn: enabled });
    onExit();
  };

  if (pickMode) {
    const isStart = pickMode === 'start';
    return (
      <S.Container>
        <EscapeButton onClick={() => setPickMode(null)} isNoBorder />
        <S.CalendarWrap>
          <S.CalendarTitle>
            {isStart ? 'Set start date' : 'Set end date'}
          </S.CalendarTitle>
          <DatePicker
            onChange={(value: any) => {
              if (isStart) {
                const picked = new Date(value);
                const next = useTimeOfDay
                  ? applyClock(picked, startD)
                  : toDayStart(picked);
                setStartD(next);
                if (+next > +endD) {
                  setEndD(useTimeOfDay ? applyClock(picked, endD) : toDayEnd(picked));
                }
              } else {
                const picked = new Date(value);
                const next = useTimeOfDay
                  ? (() => {
                    const e = applyClock(picked, endD);
                    e.setSeconds(59, 999);
                    return e;
                  })()
                  : toDayEnd(picked);
                setEndD(next);
                if (+next < +startD) {
                  setStartD(useTimeOfDay ? applyClock(picked, startD) : toDayStart(picked));
                }
              }
              setPickMode(null);
            }}
            value={isStart ? startD : endD}
            maxDate={isStart ? endD : undefined}
            minDate={isStart ? oneYearAgo : startD}
          />
          <S.CalendarActions>
            <S.SecondaryBtn type="button" onClick={() => setPickMode(null)}>
              Back
            </S.SecondaryBtn>
          </S.CalendarActions>
        </S.CalendarWrap>
      </S.Container>
    );
  }

  const startDisplay = useTimeOfDay
    ? `${convertDate22(startD)} · ${convertTime1(startD)}`
    : convertDate22(startD);
  const endDisplay = useTimeOfDay
    ? `${convertDate22(endD)} · ${convertTime1(endD)}`
    : convertDate22(endD);

  return (
    <S.Container>
      <EscapeButton onClick={() => onExit()} isNoBorder />
      <S.Header>Time range</S.Header>
      <S.Sub>Filter logs by start and end date.</S.Sub>

      <S.EnableCard>
        <S.RowLabel>Apply time range</S.RowLabel>
        <S.ToggleGroup>
          <S.ToggleBtn
            type="button"
            $on
            $active={enabled}
            onClick={() => setEnabled(true)}
          >
            On
          </S.ToggleBtn>
          <S.ToggleBtn
            type="button"
            $active={!enabled}
            onClick={() => setEnabled(false)}
          >
            Off
          </S.ToggleBtn>
        </S.ToggleGroup>
      </S.EnableCard>

      {enabled && (
        <>
          <S.Fields>
            <S.DateCard>
              <S.DateBtn type="button" onClick={() => setPickMode('start')}>
                <S.DateMeta>
                  <S.DateLabel>Start</S.DateLabel>
                  <S.DateValue>{startDisplay}</S.DateValue>
                </S.DateMeta>
                <Calendar fontSize="small" sx={{ color: S.rangeBlueInk, opacity: 0.9 }} />
              </S.DateBtn>
              {useTimeOfDay && (
                <S.TimeBlock>
                  <div>
                    <S.TimeLabel>Time of day</S.TimeLabel>
                    <S.TimeHint>HRS : MIN</S.TimeHint>
                  </div>
                  <S.TimeInputs>
                    <S.TimeInput
                      value={getTimeFormat(startD.getHours().toString())}
                      onChange={(e) => setClockPart('start', 'hours', e.target.value)}
                      inputMode="numeric"
                      aria-label="Start hours"
                    />
                    <S.TimeSep>:</S.TimeSep>
                    <S.TimeInput
                      value={getTimeFormat(startD.getMinutes().toString())}
                      onChange={(e) => setClockPart('start', 'minutes', e.target.value)}
                      inputMode="numeric"
                      aria-label="Start minutes"
                    />
                  </S.TimeInputs>
                </S.TimeBlock>
              )}
            </S.DateCard>

            <S.DateCard>
              <S.DateBtn type="button" onClick={() => setPickMode('end')}>
                <S.DateMeta>
                  <S.DateLabel>End</S.DateLabel>
                  <S.DateValue>{endDisplay}</S.DateValue>
                </S.DateMeta>
                <Calendar fontSize="small" sx={{ color: S.rangeBlueInk, opacity: 0.9 }} />
              </S.DateBtn>
              {useTimeOfDay && (
                <S.TimeBlock>
                  <div>
                    <S.TimeLabel>Time of day</S.TimeLabel>
                    <S.TimeHint>HRS : MIN</S.TimeHint>
                  </div>
                  <S.TimeInputs>
                    <S.TimeInput
                      value={getTimeFormat(endD.getHours().toString())}
                      onChange={(e) => setClockPart('end', 'hours', e.target.value)}
                      inputMode="numeric"
                      aria-label="End hours"
                    />
                    <S.TimeSep>:</S.TimeSep>
                    <S.TimeInput
                      value={getTimeFormat(endD.getMinutes().toString())}
                      onChange={(e) => setClockPart('end', 'minutes', e.target.value)}
                      inputMode="numeric"
                      aria-label="End minutes"
                    />
                  </S.TimeInputs>
                </S.TimeBlock>
              )}
            </S.DateCard>
          </S.Fields>

          <S.TimeToggle
            type="button"
            $open={useTimeOfDay}
            onClick={onToggleTimeOfDay}
          >
            <S.TimeToggleLabel>
              <AccessTime sx={{ fontSize: 16 }} />
              {useTimeOfDay ? 'Hide time of day' : 'Set time of day'}
            </S.TimeToggleLabel>
            {useTimeOfDay
              ? <ExpandLess sx={{ fontSize: 18 }} />
              : <ExpandMore sx={{ fontSize: 18 }} />}
          </S.TimeToggle>

          <S.MetricsBtn type="button" onClick={onUseMetricsTime}>
            Use metrics time
          </S.MetricsBtn>
        </>
      )}

      <S.Footer>
        <S.SecondaryBtn type="button" onClick={() => onExit()}>
          Cancel
        </S.SecondaryBtn>
        <S.PrimaryBtn type="button" onClick={onSave}>
          Save
        </S.PrimaryBtn>
      </S.Footer>
    </S.Container>
  );
};

export default TimeRangeModal;
